import { Controller, UseFormReturn } from "react-hook-form";
import { CompensationProfileFormInput } from "../types";
import ListItem from "@/components/ListItem";
import Checkbox from "@/components/Checkbox";
import { NumberFormatBase } from "react-number-format";
import TextField from "@/components/TextField";
import { currencyFormatterNoDecimals } from "@/lib/currencyHelpers";
import { useState, useEffect } from "react";
import Divider from "@/components/Divider";

const incomeCategories = [
  {
    key: "eventOuting",
    label: "I receive income from events/outings",
    amountField: "eventOutingCommissionAmount",
    percentField: "eventOutingCommissionPercent",
    amountLabel: "Event/Outing Income Amount",
    percentLabel: "Event/Outing Commission Percentage"
  },
  {
    key: "facility",
    label: "I receive income from facility revenue",
    amountField: "facilityCommissionAmount",
    percentField: "facilityCommissionPercent",
    amountLabel: "Facility Income Amount",
    percentLabel: "Facility Commission Percentage"
  },
  {
    key: "foodBeverage",
    label: "I receive income from food and beverage revenue",
    amountField: "foodBeverageCommissionAmount",
    percentField: "foodBeverageCommissionPercent",
    amountLabel: "Food and Beverage Income Amount",
    percentLabel: "Food and Beverage Commission Percentage"
  },
  {
    key: "golfCar",
    label: "I receive income from golf car revenue",
    amountField: "golfCarCommissionAmount",
    percentField: "golfCarCommissionPercent",
    amountLabel: "Golf Car Income Amount",
    percentLabel: "Golf Car Commission Percentage"
  },
  {
    key: "greenFee",
    label: "I receive income from green fee revenue",
    amountField: "greenFeeCommissionAmount",
    percentField: "greenFeeCommissionPercent",
    amountLabel: "Green Fee Income Amount",
    percentLabel: "Green Fee Commission Percentage"
  },
  {
    key: "membershipSales",
    label: "I receive income from membership sales revenue",
    amountField: "membershipSalesCommissionAmount",
    percentField: "membershipSalesCommissionPercent",
    amountLabel: "Membership Sales Income Amount",
    percentLabel: "Membership Sales Commission Percentage"
  },
  {
    key: "practiceRange",
    label: "I receive income from practice range revenue",
    amountField: "practiceRangeCommissionAmount",
    percentField: "practiceRangeCommissionPercent",
    amountLabel: "Practice Range Income Amount",
    percentLabel: "Practice Range Commission Percentage"
  },
  {
    key: "clubFitting",
    label: "I receive income from club fitting revenue",
    amountField: "clubFittingCommissionAmount",
    percentField: "clubFittingCommissionPercent",
    amountLabel: "Club Fitting Income Amount",
    percentLabel: "Club Fitting Commission Percentage"
  },
  {
    key: "clubRental",
    label: "I receive income from club rental revenue",
    amountField: "clubRentalCommissionAmount",
    percentField: "clubRentalCommissionPercent",
    amountLabel: "Club Rental Income Amount",
    percentLabel: "Club Rental Commission Percentage"
  },
  {
    key: "clubRepair",
    label: "I receive income from club repair revenue",
    amountField: "clubRepairCommissionAmount",
    percentField: "clubRepairCommissionPercent",
    amountLabel: "Club Repair Income Amount",
    percentLabel: "Club Repair Commission Percentage"
  },
  {
    key: "clubStorage",
    label: "I receive income from club storage revenue",
    amountField: "clubStorageCommissionAmount",
    percentField: "clubStorageCommissionPercent",
    amountLabel: "Club Storage Income Amount",
    percentLabel: "Club Storage Commission Percentage"
  }
];

export default function OtherIncomeDetailsStep({ form }: { form: UseFormReturn<CompensationProfileFormInput> }) {
  // Local state for checkboxes
  const [enabled, setEnabled] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    // Initialize enabled state based on form values
    const initial: { [key: string]: boolean } = {};
    incomeCategories.forEach((cat) => {
      initial[cat.key] =
        !!form.watch([cat.amountField as keyof CompensationProfileFormInput])[0] ||
        !!form.watch([cat.percentField as keyof CompensationProfileFormInput])[0];
    });
    setEnabled(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to check if an income category is enabled
  const isIncomeCategoryEnabled = (categoryKey: string): boolean => {
    return enabled[categoryKey] || false;
  };

  // Expose the enabled state checker to the parent form
  useEffect(() => {
    // @ts-ignore - adding custom property to form
    form.isIncomeCategoryEnabled = isIncomeCategoryEnabled;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, form]);

  return (
    <div className="space-y-6">
      <div className="text-headline-small">Enter additional/other income details.</div>
      <div className="col-span-full">
        <p className="text-title-medium mb-2">Check All that Apply</p>
        {incomeCategories.map((cat) => (
          <div key={cat.key} className="mb-6">
            <ListItem
              headline={cat.label}
              leading={
                <Checkbox
                  name={cat.key + "Checkbox"}
                  checked={enabled[cat.key]}
                  onChange={(checked) => {
                    setEnabled((prev) => ({ ...prev, [cat.key]: checked }));
                    // Don't clear values here - they'll be set to null on submit if disabled
                  }}
                />
              }
            />
            <div className="ps-[40px] grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="col-span-full md:col-span-2">
                <Controller
                  control={form.control}
                  name={cat.amountField as keyof CompensationProfileFormInput}
                  render={({ field }) => (
                    <NumberFormatBase
                      value={field.value as string}
                      disabled={!enabled[cat.key]}
                      name={cat.amountField}
                      className="w-full"
                      customInput={TextField}
                      error={!!form.formState.errors[cat.amountField as keyof CompensationProfileFormInput]}
                      errorText={
                        form.formState.errors[cat.amountField as keyof CompensationProfileFormInput]?.message as string
                      }
                      onFocus={(e) => {
                        e.target.select();
                      }}
                      format={currencyFormatterNoDecimals}
                      onValueChange={(values) => {
                        field.onChange(values.value ? parseInt(values.value, 10) : null);
                        form.trigger(cat.amountField as keyof CompensationProfileFormInput);
                      }}
                      leadingIcon="attach_money"
                      label={cat.amountLabel}
                      placeholder={cat.amountLabel}
                    />
                  )}
                />
              </div>
              <div className="col-span-full md:col-span-2">
                <Controller
                  control={form.control}
                  name={cat.percentField as keyof CompensationProfileFormInput}
                  render={({ field }) => (
                    <NumberFormatBase
                      value={field.value as string}
                      disabled={!enabled[cat.key]}
                      name={cat.percentField}
                      className="w-full"
                      customInput={TextField}
                      error={!!form.formState.errors[cat.percentField as keyof CompensationProfileFormInput]}
                      errorText={
                        form.formState.errors[cat.percentField as keyof CompensationProfileFormInput]?.message as string
                      }
                      onFocus={(e) => {
                        e.target.select();
                      }}
                      onValueChange={(values) => {
                        field.onChange(values.floatValue ?? null);
                        form.trigger(cat.percentField as keyof CompensationProfileFormInput);
                      }}
                      leadingIcon="percent"
                      label={cat.percentLabel}
                      placeholder={cat.percentLabel}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="py-3">
        <Divider />
      </div>
      <div className="flex justify-between gap-4">
        <p className="text-title-medium">Total Other Income</p>
        <p className="text-title-medium">
          {(() => {
            const total = incomeCategories.reduce((sum, cat) => {
              const val = form.getValues(cat.amountField as keyof CompensationProfileFormInput);
              return sum + Number(val);
            }, 0);
            return total.toLocaleString("en-US", { style: "currency", currency: "USD" });
          })()}
        </p>
      </div>
    </div>
  );
}
